import React from 'react';
import { flushSync } from 'react-dom';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { ModalBody, ModalFooter, ModalHeader, Alert, FormGroup, Input, Label } from 'reactstrap';
import CancellableModal from './CancellableModal';
import BusyButton from './BusyButton';
import { idGenerator } from '~/lib/helper/IdHelper';
import type { ButtonColor } from '~/lib/UiTypes';

type ConfirmModalProps = {
    title: string;
    message: React.ReactNode;
    warning?: React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: ButtonColor;
    acknowledgement?: {
        label: React.ReactNode;
    };
    // Optional async action. When provided, the modal stays open with a busy
    // state while it runs; on rejection it renders an inline error and re-enables
    // the buttons so the customer can retry. When omitted, `onConfirmed` fires
    // immediately on confirm (the original boolean-resolving behaviour).
    perform?: () => Promise<unknown>;
    // Renders the inline error shown when `perform` rejects. Defaults to a
    // generic message.
    renderError?: (error: unknown) => React.ReactNode;
    // Fired when the modal should close as "confirmed": immediately in the legacy
    // path, or after `perform` resolves.
    onConfirmed: () => unknown;
    onCancel: () => unknown;
};

const acknowledgementIdGen = idGenerator('confirmModalAcknowledgement');
const titleIdGen = idGenerator('confirm-modal-title-');
const messageIdGen = idGenerator('confirm-modal-message-');
const warningIdGen = idGenerator('confirm-modal-warning-');

const defaultErrorMessage = <p>Something went wrong. Please try again.</p>;

// Stack of mounted instances so the global Enter/Escape handler only fires on
// the topmost modal when ConfirmModals are stacked.
const mountedInstances: ConfirmModal[] = [];

/**
 * The acknowledgement tick state is reset on mount only. Callers reusing the
 * same instance across multiple prompts must pass a fresh React `key` per
 * prompt to force a remount; `ConfirmModalHost` does this implicitly by
 * unmounting the modal between confirms.
 */
@observer
export default class ConfirmModal extends React.Component<ConfirmModalProps> {
    private readonly titleId = titleIdGen();
    private readonly messageId = messageIdGen();
    private readonly warningId = warningIdGen();
    private readonly acknowledgementId = acknowledgementIdGen();
    // Refs for restoring focus after a failed attempt: the busy-disable blurs the
    // active control to <body>, so handleConfirm re-focuses a button on rejection.
    private readonly confirmButtonRef = React.createRef<HTMLButtonElement>();
    private readonly cancelButtonRef = React.createRef<HTMLButtonElement>();

    @observable
    acknowledged = false;

    // Set while `perform` is in flight — disables both buttons (via
    // BusyButton / CancellableModal) and gates the keyboard handler so nothing
    // dismisses or re-triggers mid-call.
    @observable
    busy = false;

    // Inline error rendered above the footer after `perform` rejects.
    // `.ref` so MobX stores the ReactNode by reference — a deep observable would
    // recurse the element tree and overflow the stack.
    @observable.ref
    error: React.ReactNode | undefined = undefined;

    constructor(props: ConfirmModalProps) {
        super(props);
        makeObservable(this);
    }

    // Enter and Escape are both handled here at the document level, not scoped to focus
    // inside the modal: ConfirmModal is a quick yes/no over a parent flow and focus may
    // not be in the dialog yet. reactstrap's own focus-scoped Escape is disabled via
    // `keyboard={false}` (see render) so it can't double-fire onCancel after this handler.
    handleKeyDown = (e: KeyboardEvent) => {
        if (mountedInstances[mountedInstances.length - 1] !== this) return;

        // While an async confirm is in flight, mirror the disabled buttons and
        // static backdrop: neither Escape (cancel) nor Enter (confirm) should act.
        if (this.busy) return;

        if (e.key === 'Escape') {
            // Escape always dismisses; no native activation collision.
            e.preventDefault();
            this.props.onCancel();
            return;
        }
        if (e.key !== 'Enter') return;

        // Skip the document-level confirm when the target handles Enter
        // natively (form controls, buttons, links). `target` may be `document`
        // when dispatched on the document directly — guard with instanceof.
        const target = e.target;
        if (
            target instanceof Element &&
            target.closest(
                'input, select, textarea, button, a[href], [role="button"], [role="link"], [contenteditable="true"]'
            )
        ) {
            return;
        }
        e.preventDefault();
        if (this.canConfirm) {
            void this.handleConfirm();
        }
    };

    componentDidMount() {
        mountedInstances.push(this);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        const idx = mountedInstances.indexOf(this);
        if (idx !== -1) mountedInstances.splice(idx, 1);
    }

    get canConfirm(): boolean {
        return !this.props.acknowledgement || this.acknowledged;
    }

    @action
    setAcknowledged = (value: boolean) => {
        this.acknowledged = value;
    };

    // Dismissal (Cancel / header X / backdrop). Busy-guarded like handleConfirm so a
    // rapid dismissal mid-action can't cancel while the action is still running.
    handleCancel = () => {
        if (this.busy) return;
        this.props.onCancel();
    };

    // Confirm handler shared by the footer button and document-level Enter.
    // Legacy callers (no `perform`) resolve immediately; async callers get
    // the busy/inline-error lifecycle.
    handleConfirm = async (): Promise<void> => {
        // Synchronous re-entrancy guard: a rapid second click (or Enter) can land
        // before React commits the busy-disabled button, so gate on the observable
        // directly rather than trusting the rendered `disabled` attribute — else a
        // destructive action could dispatch twice.
        if (this.busy) return;
        const { perform, renderError, onConfirmed } = this.props;
        if (!perform) {
            onConfirmed();
            return;
        }

        runInAction(() => {
            this.busy = true;
            this.error = undefined;
        });
        try {
            await perform();
        } catch (e) {
            // Stay open: surface the error inline and re-enable for retry/cancel. flushSync so the
            // re-enabled button exists to focus below — focus() on a still-disabled button is a no-op.
            // See PanelSite.md "React 18 commit timing".
            flushSync(() => {
                runInAction(() => {
                    this.busy = false;
                    this.error = renderError?.(e) ?? defaultErrorMessage;
                });
            });
            // Restore focus after the busy-disable blurred the active control to <body>:
            // Cancel for a danger confirm (the safer default so a stray Enter can't re-fire
            // the destructive action), Confirm otherwise.
            const focusCancel = this.props.confirmColor === 'danger';
            (focusCancel ? this.cancelButtonRef : this.confirmButtonRef).current?.focus();
            return;
        }
        // Success: hand back to the host, which unmounts this modal and fires
        // onSuccess. Clear busy first so the observable stays consistent.
        runInAction(() => {
            this.busy = false;
        });
        onConfirmed();
    };

    render() {
        const {
            title,
            message,
            warning,
            confirmText = 'Confirm',
            cancelText = 'Cancel',
            confirmColor = 'primary',
            acknowledgement,
        } = this.props;
        // Acknowledgement modals defer focus to Reactstrap's dialog wrapper —
        // the checkbox is the safer gate (Enter does nothing until ticked), so
        // we don't pre-focus Cancel for danger. Without acknowledgement, defer
        // to button autoFocus (Cancel for danger, Confirm otherwise).
        const modalAutoFocus = !!acknowledgement;
        return (
            <CancellableModal
                isOpen
                toggle={this.handleCancel}
                onCancel={this.handleCancel}
                cancelText={cancelText}
                cancelButtonRef={this.cancelButtonRef}
                busy={this.busy}
                autoFocus={modalAutoFocus}
                autoFocusCancel={confirmColor === 'danger' && !acknowledgement}
                labelledBy={this.titleId}
                // ConfirmModal owns Enter+Escape at the document level (mountedInstances stack
                // gates the topmost-only case). Opt out of reactstrap's focus-scoped Escape so
                // it doesn't double-fire onCancel after our handler does.
                keyboard={false}
            >
                <ModalHeader>
                    {/* id on the title span, not the ModalHeader — see PanelSite.md "Modals". */}
                    <span id={this.titleId}>{title}</span>
                </ModalHeader>
                <ModalBody>
                    {warning && (
                        <Alert id={this.warningId} color="warning">
                            <h5 className="alert-alert-heading">
                                <i className="icon ion-md-alert text-danger"></i> Warning!
                            </h5>
                            {warning}
                        </Alert>
                    )}
                    <div id={this.messageId}>{message}</div>
                    {acknowledgement && (
                        <FormGroup check className="mt-3">
                            <Input
                                id={this.acknowledgementId}
                                type="checkbox"
                                aria-describedby={[warning && this.warningId, this.messageId].filter(Boolean).join(' ')}
                                checked={this.acknowledged}
                                disabled={this.busy}
                                onChange={(e) => this.setAcknowledged(e.target.checked)}
                            />
                            <Label for={this.acknowledgementId} check>
                                {acknowledgement.label}
                            </Label>
                        </FormGroup>
                    )}
                    {this.error && (
                        <Alert color="danger" className="mt-3">
                            {this.error}
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter>
                    {/* WAI-ARIA dialog pattern: destructive flows focus the safer Cancel button.
                        Cancel itself is injected by CancellableModal (autoFocusCancel above);
                        we keep Confirm's autoFocus when the modal is non-danger and not
                        acknowledgement-gated. Acknowledgement modals skip both — the
                        checkbox is the safer gate. */}
                    <BusyButton
                        innerRef={this.confirmButtonRef}
                        color={confirmColor}
                        busy={this.busy}
                        onClick={() => void this.handleConfirm()}
                        autoFocus={confirmColor !== 'danger' && !acknowledgement}
                        disabled={!this.canConfirm}
                    >
                        {confirmText}
                    </BusyButton>
                </ModalFooter>
            </CancellableModal>
        );
    }
}
